import { useState } from 'react'
import { formatDate } from '../../utils/date-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { CorrelationCanvas } from './correlation-canvas'
import { kindLabel, toolColorToken, toolDisplayName } from './exploit-chains-palette.ts'
import * as styles from './exploit-chains-run-summary.css.ts'
import { countVerdicts, droppedForBudget, isFullyCovered, lensLabel, type RunState } from './exploit-chains-run.ts'
import { HypothesisLog } from './hypothesis-log'

/**
 * What a completed run shows instead of "0 chains": what was tested across which inputs, how
 * much of the repository that covered, and, folded underneath, every hypothesis with its verdict.
 * The canvas at rest sits beside it so the picture the reader watched while waiting is the picture
 * they come back to.
 */

const listNames = (names: string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

const formatSeconds = (seconds: number): string => {
  const whole = Math.round(seconds)
  const minutes = Math.floor(whole / 60)
  const rest = whole % 60
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`
}

const coverageSentence = (run: RunState): string => {
  const tools = run.tools ?? []
  const total = run.totalFindings ?? 0
  const indexed = run.indexed ?? total
  const dropped = droppedForBudget(run)
  const failedLenses = run.lenses.filter(lens => lens.state === 'failed')
  const sources = tools.length > 0 ? `${listNames(tools.map(tool => toolDisplayName(tool.tool)))} results` : 'Results'
  const combined = `${sources} were combined with the repository’s threat model${
    run.threatModelGeneratedAt ? ` from ${formatDate({ date: run.threatModelGeneratedAt, type: 'mm/dd/yyyy' })}` : ''
  }.`
  const coverage =
    dropped > 0
      ? ` ${indexed} of ${total} findings fit the model’s context; ${dropped} ${pluralize(dropped, 'was', 'were')} never indexed, so this result does not cover ${pluralize(dropped, 'it', 'them')}.`
      : total === 1
        ? ' The one finding fit the model’s context.'
        : ` All ${total} findings fit the model’s context.`
  const lenses =
    failedLenses.length > 0
      ? ` ${listNames(failedLenses.map(lens => lensLabel(lens.lens)))} failed, so some proposals are missing.`
      : ''
  return combined + coverage + lenses
}

export type ExploitChainsRunSummaryProps = {
  run: RunState
}

export const ExploitChainsRunSummary: React.FC<ExploitChainsRunSummaryProps> = ({ run }) => {
  const [highlightedId, setHighlightedId] = useState<string>()
  const tools = run.tools ?? []
  const total = run.totalFindings ?? 0
  const verdicts = countVerdicts(run)
  const tested = run.hypotheses.length
  const dropped = droppedForBudget(run)
  const hasLog = run.hypotheses.length > 0 || run.pool !== undefined

  return (
    <section className={styles.panel} aria-label="How this result was reached">
      <div className={styles.body}>
        <p className={styles.headline}>
          Tested <span className={styles.number}>{tested}</span> attack {pluralize(tested, 'hypothesis', 'hypotheses')}{' '}
          across <span className={styles.number}>{total}</span> {pluralize(total, 'finding', 'findings')} from{' '}
          {tools.length} {pluralize(tools.length, 'tool', 'tools')}.{' '}
          {verdicts.confirmed > 0 ? (
            <>
              <span className={styles.number}>{verdicts.confirmed}</span> held up under verification.
            </>
          ) : tested > 0 ? (
            'None held up under verification.'
          ) : (
            'No hypothesis was worth verifying.'
          )}
        </p>
        <p className={styles.coverage}>{coverageSentence(run)}</p>
        <div
          className={styles.compositionBar}
          role="img"
          aria-label={`Findings by tool: ${tools.map(tool => `${toolDisplayName(tool.tool)} ${tool.count}`).join(', ')}`}
        >
          {tools.map((tool, index) => (
            <span
              key={tool.tool}
              className={styles.compositionSegment}
              style={{ width: `${total > 0 ? (tool.count / total) * 100 : 0}%`, background: toolColorToken(index) }}
            />
          ))}
        </div>
        <ul className={styles.compositionLabels} aria-label="Tools">
          {tools.map((tool, index) => (
            <li key={tool.tool} className={styles.compositionLabel}>
              <span className={styles.swatch} style={{ background: toolColorToken(index) }} aria-hidden="true" />
              {toolDisplayName(tool.tool)}
              {kindLabel(tool.kind) ? ` · ${kindLabel(tool.kind)}` : ''} {tool.count}
            </li>
          ))}
          {dropped > 0 && (
            <li className={styles.compositionLabel}>
              <span className={styles.swatchHollow} aria-hidden="true" />
              {dropped} never indexed
            </li>
          )}
        </ul>
        <dl className={styles.tiles}>
          <Tile
            label="Hypotheses tested"
            value={`${tested}`}
            detail={
              run.pool
                ? `from ${run.pool.raw} ${pluralize(run.pool.raw, 'proposal', 'proposals')} across ${run.lenses.length} ${pluralize(run.lenses.length, 'lens', 'lenses')}`
                : ''
            }
          />
          <Tile
            label="Held"
            value={`${verdicts.confirmed}`}
            detail={
              verdicts.confirmed > 0
                ? 'confirmed against the source'
                : tested > 0
                  ? 'every one rejected on the source'
                  : 'nothing reached verification'
            }
          />
          <Tile
            label="Coverage"
            value={`${run.indexed ?? total} / ${total}`}
            detail={isFullyCovered(run) ? 'every finding considered' : 'findings in the model’s context'}
          />
          <Tile
            label="Correlation time"
            value={run.correlationSeconds !== undefined ? formatSeconds(run.correlationSeconds) : '—'}
            detail={`${run.lenses.length} proposal ${pluralize(run.lenses.length, 'pass', 'passes')} + ${tested} ${pluralize(tested, 'verification', 'verifications')}`}
          />
        </dl>
        {hasLog && (
          <details className={styles.details}>
            <summary className={styles.summary}>
              {tested > 0
                ? `Show the ${tested} ${pluralize(tested, 'hypothesis', 'hypotheses')} and ${verdicts.confirmed > 0 ? 'their verdicts' : 'why they were rejected'}`
                : 'Show how the proposals were narrowed'}
            </summary>
            <HypothesisLog
              run={run}
              className={styles.log}
              highlightedId={highlightedId}
              onHighlightHypothesis={setHighlightedId}
            />
          </details>
        )}
      </div>
      <div className={styles.aside}>
        <CorrelationCanvas run={run} size="small" highlightedId={highlightedId} />
        <p className={styles.asideCaption}>
          {verdicts.confirmed > 0
            ? 'Chains that held, over the findings they connect'
            : tested > 0
              ? 'Hypotheses that did not hold, drawn as ghosts'
              : 'Every finding the run considered, by tool'}
        </p>
      </div>
    </section>
  )
}

const Tile: React.FC<{ label: string; value: string; detail: string }> = ({ label, value, detail }) => (
  <div className={styles.tile}>
    <dt className={styles.tileLabel}>{label}</dt>
    <dd className={styles.tileValue}>{value}</dd>
    <dd className={styles.tileDetail}>{detail}</dd>
  </div>
)
